import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Heart,
  Lock,
  Menu,
  Package,
  Search,
  ShoppingBag,
  User,
  X,
} from "lucide-react";

import {
  get_cart_products,
  get_wishlist_products,
} from "../store/reducers/cardReducer";
import { setStoreSearchKeyword, setStoreCategoryFilter } from "../store/reducers/homeReducer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { FaLock, FaUser } from "react-icons/fa";
import { AiFillHeart, AiFillShopping } from "react-icons/ai";
import { areStoreNamesEqual } from "../utils/storeUtils";

const Headers = ({ storeName: propStoreName, isPreview, customSettings }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categorys, products, seller, product } = useSelector((state) => state.home);
  const { userInfo } = useSelector((state) => state.auth);
  const { card_product_count, wishlist_count, card_products, wishlist } = useSelector(
    (state) => state.card
  );

  const location = useLocation();
  const { pathname } = location;
  const { storeName, storename } = useParams();

  const productSlug = location.pathname.includes('/product/details/') ? location.pathname.split('/').pop() : null;
  const storeToPass = propStoreName || storeName || storename || location.state?.fromStore || (productSlug ? sessionStorage.getItem(`product-store-${productSlug}`) : null);

  const filteredCardCount = storeToPass
    ? card_products.find(p => areStoreNamesEqual(p.shopName, storeToPass))?.products.reduce((acc, item) => acc + item.quantity, 0) || 0
    : card_product_count;

  const filteredWishlistCount = storeToPass
    ? wishlist.filter(item => areStoreNamesEqual(item.shopName, storeToPass)).length
    : wishlist_count;

  const [showShidebar, setShowShidebar] = useState(true);
  const [categoryShow, setCategoryShow] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [category, setCategory] = useState("");
  const [storeCategories, setStoreCategories] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    if (storeToPass && products) {
      const uniqueCategories = [...new Set(products.map(p => p.category))];
      setStoreCategories(uniqueCategories.map(c => ({ name: c })));
    }
  }, [storeToPass, products]);

  const search = () => {
    if (storeToPass) {
      setIsSearching(true);
      setTimeout(() => {
        const productsSection = document.getElementById('store-products');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth' });
        } else {
          // If we are not on the store page, navigate to it
          navigate(`/${storeToPass}`);
        }
        setIsSearching(false);
      }, 500);
      return;
    }
    navigate(`/products/search?category=${encodeURIComponent(category)}&value=${encodeURIComponent(searchValue)}`);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (storeToPass) {
      dispatch(setStoreSearchKeyword(value));
    }
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    if (storeToPass) {
      dispatch(setStoreCategoryFilter(value === "All" ? "" : value));
    }
  };

  const redirect_cart_page = () => {
    const cartPath = storeToPass ? `/${storeToPass}/cart` : "/cart";
    navigate(cartPath, { state: { fromStore: storeToPass } });
  };
  const initial = () => {
    navigate("/");
  };
  useEffect(() => {
    if (userInfo) {
      // If we are in a store context (storeToPass is set), we pass it to the actions
      // This allows the reducer to decide whether to fetch global user data or store-specific data
      // However, the reducer logic I added earlier for get_cart_products handles { userId, storeName }
      // But here we are dispatching.

      // Wait, if I am logged in, and I am in a store, the user wants it to appear as "not logged in".
      // So maybe I should NOT fetch the user's global cart if storeToPass is present?
      // Or I should fetch the store-specific cart using the storeName.

      if (storeToPass) {
        dispatch(get_cart_products({ userId: userInfo.id, storeName: storeToPass }));
        dispatch(get_wishlist_products({ userId: userInfo.id, storeName: storeToPass }));
      } else {
        dispatch(get_cart_products(userInfo.id));
        dispatch(get_wishlist_products(userInfo.id));
      }
    } else {
      // Guest user
      if (storeToPass) {
        dispatch(get_cart_products({ userId: null, storeName: storeToPass }));
        dispatch(get_wishlist_products({ userId: null, storeName: storeToPass }));
      } else {
        dispatch(get_cart_products(null));
        dispatch(get_wishlist_products(null));
      }
    }
  }, [userInfo, storeToPass]);

  const headerStyle = {};
  if (storeToPass && customSettings && Object.keys(customSettings).length > 0) {
    // Header colors
    if (customSettings.backgroundColor) {
      headerStyle['--store-header-bg'] = customSettings.backgroundColor;
    }
    if (customSettings.textColor) {
      headerStyle['--store-text'] = customSettings.textColor;
    }
    if (customSettings.iconColor) {
      headerStyle['--store-icon'] = customSettings.iconColor;
    }

    // Input styling overrides
    if (customSettings.inputBg) {
      headerStyle['--store-input-bg'] = customSettings.inputBg;
    }
    if (customSettings.inputText) {
      headerStyle['--store-input-text'] = customSettings.inputText;
    }
    if (customSettings.inputBorder) {
      headerStyle['--store-input-border'] = customSettings.inputBorder;
    }
    if (customSettings.inputFocus) {
      headerStyle['--store-input-focus'] = customSettings.inputFocus;
    }
    if (customSettings.inputRadius !== undefined) {
      headerStyle['--store-input-radius'] = `${customSettings.inputRadius}px`;
    }

    // Select styling overrides
    if (customSettings.selectBg) {
      headerStyle['--store-select-bg'] = customSettings.selectBg;
    }
    if (customSettings.selectText) {
      headerStyle['--store-select-text'] = customSettings.selectText;
    }
    if (customSettings.selectBorder) {
      headerStyle['--store-select-border'] = customSettings.selectBorder;
    }
    if (customSettings.selectFocus) {
      headerStyle['--store-select-focus'] = customSettings.selectFocus;
    }
    if (customSettings.selectRadius !== undefined) {
      headerStyle['--store-select-radius'] = `${customSettings.selectRadius}px`;
    }

    // Button styling overrides
    if (customSettings.buttonBg) {
      headerStyle['--store-button-bg'] = customSettings.buttonBg;
    }
    if (customSettings.buttonText) {
      headerStyle['--store-button-text'] = customSettings.buttonText;
    }
  }

  return (
    <>
      <div
        className={`w-full ${storeToPass ? "sticky top-0 z-[99999] bg-[var(--store-header-bg)]" : ""}`}
        style={headerStyle}
      >
        {storeToPass && (
          <div className="w-full bg-[var(--store-text)] text-[var(--store-bg)] overflow-hidden py-2 border-b border-[var(--store-border)] hidden">
            <style>
              {`
              @keyframes marquee {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .animate-marquee {
                display: flex;
                width: max-content;
                animation: marquee 20s linear infinite;
              }
              .animate-marquee:hover {
                animation-play-state: paused;
              }
            `}
            </style>
            <div className="animate-marquee text-xs font-semibold tracking-widest uppercase flex items-center whitespace-nowrap">
              <div className="flex items-center gap-12 px-6 shrink-0">
                <span>GET DISCOUNTS ON ORDERS ABOVE 100,000 NGN WHEN YOU SHOP IN THE NEXT 1 HOUR 📦 </span>
              </div>
              <div className="flex items-center gap-12 px-6 shrink-0">
                <span>GET DISCOUNTS ON ORDERS ABOVE 100,000 NGN WHEN YOU SHOP IN THE NEXT 1 HOUR 📦 </span>
              </div>
              <div className="flex items-center gap-12 px-6 shrink-0">
                <span>GET DISCOUNTS ON ORDERS ABOVE 100,000 NGN WHEN YOU SHOP IN THE NEXT 1 HOUR 📦 </span>
              </div>
              <div className="flex items-center gap-12 px-6 shrink-0">
                <span>GET DISCOUNTS ON ORDERS ABOVE 100,000 NGN WHEN YOU SHOP IN THE NEXT 1 HOUR 📦 </span>
              </div>
              <div className="flex items-center gap-12 px-6 shrink-0">
                <span>GET DISCOUNTS ON ORDERS ABOVE 100,000 NGN WHEN YOU SHOP IN THE NEXT 1 HOUR 📦 </span>
              </div>
              <div className="flex items-center gap-12 px-6 shrink-0">
                <span>GET DISCOUNTS ON ORDERS ABOVE 100,000 NGN WHEN YOU SHOP IN THE NEXT 1 HOUR 📦 </span>
              </div>
            </div>
          </div>
        )}
        <div className="w-[85%] lg:w-[90%] mx-auto py-5">
          <div className="flex w-full flex-wrap justify-between md-lg:gap-4">
            <div className="flex justify-between items-center md-lg:w-full">
              {storeToPass ? (
                <>
                  <style>
                    {`@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700&display=swap');`}
                  </style>
                  <div onClick={() => {
                    if (isPreview) {
                      window.location.href = '/store-preview';
                    } else if (location.pathname === '/store-preview') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      navigate(`/${storeToPass}`);
                    }
                  }} className="cursor-pointer group">
                    <h1
                      className="text-2xl font-bold text-[var(--store-text)] tracking-wider group-hover:opacity-80 transition-all duration-300"
                      style={{ fontFamily: "'Cinzel', serif" }}
                    >
                      {(() => {
                        const activeSeller = seller || (location.pathname.includes('/product/details') ? product?.sellerInfo : null);
                        return activeSeller?.shopInfo?.logoText || activeSeller?.shopInfo?.shopName?.replace(/-/g, ' ') || storeToPass?.replace(/-/g, ' ') || "";
                      })()}
                    </h1>
                  </div>
                </>
              ) : (
                <img
                  onClick={initial}
                  className="w-[90px] h-auto cursor-pointer"
                  src={`${process.env.REACT_APP_URL}/images/stores.svg`}
                  alt="logo"
                />
              )}

              <div className={`relative after:h-[25px] hidden ${(!storeToPass || mobileMenu) ? 'md-lg:block' : ''}`}>
                <Select onValueChange={handleCategoryChange}>
                  <SelectTrigger className="w-full text-[var(--store-select-text)] bg-[var(--store-select-bg)] font-semibold px-5 h-[40px] border-[var(--store-select-border)] store-select-focus" style={{ borderRadius: 'var(--store-select-radius, 20px)', borderWidth: 'var(--store-select-border-width, 1px)' }}>
                    <SelectValue placeholder="Categories" />
                  </SelectTrigger>
                  <SelectContent className="nex_drop z-[100000] bg-[var(--store-card-bg)] border-[var(--store-border)]">
                    {storeToPass && <SelectItem value="All" className="hover:text-[var(--store-text)] cursor-pointer text-[var(--store-text)]">All Categories</SelectItem>}
                    {(storeToPass ? storeCategories : categorys)
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((c, i) => (
                        <SelectItem
                          className="hover:text-[var(--store-text)] cursor-pointer text-[var(--store-text)]"
                          key={i}
                          value={c.name}
                        >
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {storeToPass && !mobileMenu && (
                <div className="hidden md-lg:flex items-center gap-4">
                  <div
                    onClick={() => {
                      const wishlistPath = storeToPass ? `/${storeToPass}/wishlist` : (userInfo ? "/dashboard/my-wishlist" : "/wishlist");
                      navigate(wishlistPath, { state: { fromStore: storeToPass } });
                    }}
                    className="relative flex justify-center items-center cursor-pointer w-[35px] h-[35px]"
                  >
                    <span className="text-xl text-[var(--store-icon)]">
                      <Heart size={20} />
                    </span>
                    {filteredWishlistCount !== 0 && (
                      <div className="w-[25px] h-[25px] absolute rounded-full text-[10px] bg-red-600 text-white flex justify-center items-center -top-[5px] -right-[7px]">
                        {filteredWishlistCount}
                      </div>
                    )}
                  </div>

                  <div
                    onClick={redirect_cart_page}
                    className="relative flex justify-center items-center cursor-pointer w-[35px] h-[35px]"
                  >
                    <span className="text-xl text-[var(--store-icon)]">
                      <ShoppingBag size={20} />
                    </span>
                    {filteredCardCount !== 0 && (
                      <div className="w-[25px] h-[25px] absolute rounded-full text-white text-[10px] bg-red-600  flex justify-center items-center -top-[5px] -right-[7px]">
                        {filteredCardCount}
                      </div>
                    )}
                  </div>

                  <div
                    className="cursor-pointer text-[var(--store-icon)]"
                    onClick={() => setMobileMenu(true)}
                  >
                    <Menu size={30} />
                  </div>
                </div>
              )}
            </div>

            <div className={`w-8/12 pl-8 md-lg:pl-0 md-lg:w-full ${storeToPass && !mobileMenu ? 'md-lg:hidden' : ''}`}>
              <div className="flex flex-wrap w-full justify-center items-center md-lg:gap-2">
                <div className="w-10/12 md-lg:w-full">
                  <div className="flex h-[50px] items-center relative gap-3">
                    <div className="md:hidden relative after:h-[25px]">
                      <Select onValueChange={handleCategoryChange}>
                        <SelectTrigger className="w-full text-[var(--store-select-text)] bg-[var(--store-select-bg)] font-semibold px-5 h-[40px] border-[var(--store-select-border)] store-select-focus" style={{ borderRadius: 'var(--store-select-radius, 20px)', borderWidth: 'var(--store-select-border-width, 1px)' }}>
                          <SelectValue placeholder="Categories" />
                        </SelectTrigger>
                        <SelectContent className="nex_drop z-[100000] bg-[var(--store-card-bg)] border-[var(--store-border)]">
                          {storeToPass && <SelectItem value="All" className="hover:text-[var(--store-text)] cursor-pointer text-[var(--store-text)]">All Categories</SelectItem>}
                          {(storeToPass ? storeCategories : categorys)
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((c, i) => (
                              <SelectItem
                                className="hover:text-[var(--store-text)] cursor-pointer text-[var(--store-text)]"
                                key={i}
                                value={c.name}
                              >
                                {c.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      className="h-[40px] bg-[var(--store-input-bg)] px-5 placeholder:text-[var(--store-input-text)] text-[var(--store-input-text)] border-[var(--store-input-border)] store-input-focus"
                      style={{ borderRadius: 'var(--store-input-radius, 20px)', borderWidth: 'var(--store-input-border-width, 1px)' }}
                      onChange={handleSearchChange}
                      type="text"
                      placeholder={storeToPass ? `Search in ${storeToPass.replace(/-/g, ' ')}` : "Search for products & merchants"}
                    />
                    <Button
                      onClick={search}
                      type="submit"
                      disabled={isSearching}
                      className="bg-[var(--store-button-bg)] text-[var(--store-button-text)] hover:bg-[var(--store-button-bg)] hover:opacity-90"
                      style={customSettings?.buttonRadius !== undefined ? { borderRadius: `${customSettings.buttonRadius}px` } : undefined}
                    >
                      {isSearching ? <div className="w-4 h-4 border-2 border-[var(--store-button-text)] border-t-transparent rounded-full animate-spin"></div> : "Search"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className={`flex items-center justify-between md:w-full sm:justify-between sm:w-full ${storeToPass && !mobileMenu ? 'md-lg:hidden' : ''}`}>
              {!storeToPass && (
                <Link to={process.env.REACT_APP_SELLER_DASHBOARD_URL}>
                  <button className="flex items-center bg-white nex_button button_100 font-semibold text-white hover:bg-white hover:text-black h-[40px]">
                    {" "}
                    Sell online <Package className="ml-2" size={24} />{" "}
                  </button>
                </Link>
              )}
              <div className={`flex vp45 items-center ${storeToPass ? 'w-[100%] justify-between' : ''}`}>
                {storeToPass ? (
                  <div className="ml-5 flex justify-center items-center gap-2 text-sm text-[var(--store-text)]">
                    <span>
                      <Lock size={18} />
                    </span>
                    <span>Secured</span>
                  </div>
                ) : userInfo ? (
                  <Link
                    className="flex cursor-pointer justify-center items-center gap-2 text-sm ml-5"
                    to="/dashboard"
                  >
                    <span></span>
                    <span>{userInfo.name}</span>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    state={{ fromStore: storeToPass }}
                    className="ml-5 flex cursor-pointer justify-center items-center gap-2 text-sm"
                  >
                    <span>
                      <Lock size={18} />
                    </span>
                    <span>Login</span>
                  </Link>
                )}
                <div className="flex justify-center ml-2">
                  <div
                    onClick={() => {
                      const wishlistPath = storeToPass ? `/${storeToPass}/wishlist` : (userInfo ? "/dashboard/my-wishlist" : "/wishlist");
                      navigate(wishlistPath, { state: { fromStore: storeToPass } });
                    }}
                    className="relative flex justify-center items-center cursor-pointer w-[35px] h-[35px]"
                  >
                    <span className="text-xl text-[var(--store-icon)]">
                      <Heart size={20} />
                    </span>
                    {filteredWishlistCount !== 0 && (
                      <div className="w-[25px] h-[25px] absolute rounded-full text-[10px] bg-red-600 text-white flex justify-center items-center -top-[5px] -right-[7px]">
                        {filteredWishlistCount}
                      </div>
                    )}
                  </div>

                  <div
                    onClick={redirect_cart_page}
                    className="relative flex justify-center items-center cursor-pointer w-[35px] h-[35px]"
                  >
                    <span className="text-xl text-[var(--store-icon)]">
                      <ShoppingBag size={20} />
                    </span>
                    {filteredCardCount !== 0 && (
                      <div className="w-[25px] h-[25px] absolute rounded-full text-white text-[10px] bg-red-600  flex justify-center items-center -top-[5px] -right-[7px]">
                        {filteredCardCount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {mobileMenu && storeToPass && (
            <div className="w-full flex justify-center mt-4 animate-in slide-in-from-top-5 fade-in">
              <button
                onClick={() => setMobileMenu(false)}
                className="bg-[var(--store-glass)] backdrop-blur-md border border-[var(--store-border)] text-[var(--store-text)] px-4 py-2 rounded-full flex items-center gap-2 hover:bg-[var(--store-glass)] transition-all shadow-lg"
              >
                <X size={16} />
                <span className="text-sm font-medium">Close Menu</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Headers;
